import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { PERIOD, MEASUREMENT_UNITS } from './constants';
/**
 * http://stackoverflow.com/questions/2353211/hsl-to-rgb-color-conversion
 *
 * Converts an HSL color value to RGB. Conversion formula
 * adapted from http://en.wikipedia.org/wiki/HSL_color_space.
 * Assumes h, s, and l are contained in the set [0, 1] and
 * returns r, g, and b in the set [0, 255].
 *
 * @param   Number  h       The hue
 * @param   Number  s       The saturation
 * @param   Number  l       The lightness
 * @return  Array           The RGB representation
 */

// convert a color to a number using hsl
// based on formula as provided by @KamilT

dayjs.extend(relativeTime);

export function numberToColorRgb(i) {
    // we calculate red and green
    var red = Math.floor(0 + (255 * i) / 100);
    var green = Math.floor((255 * i) / 100);
    // we format to css value and return
    return 'rgb(' + red + ',' + green + ',255)';
}

export function strPeriodToFormattedDate(str, format, customDate) {
    switch (str) {
        case PERIOD.TODAY:
            return getTimeAdjustedDate(0, 0, 0).format(format);
        case PERIOD.END_TODAY:
            return getTimeAdjustedDate(23, 59, 59).format(format);
        case PERIOD.THIS_MONTH:
            return getTimeAdjustedDate(0, 0, 0)
                .date(1)
                .format(format);
        case PERIOD.THIS_YEAR:
            return getTimeAdjustedDate(0, 0, 0)
                .month(0)
                .date(1)
                .format(format);
        case PERIOD.LAST_YEAR:
            return getTimeAdjustedDate(0, 0, 0)
                .month(0)
                .date(1)
                .subtract(1, 'year')
                .format(format);
        case PERIOD.LAST_YEAR_END:
            return getTimeAdjustedDate(23, 59, 59)
                .month(11)
                .date(31)
                .subtract(1, 'year')
                .format(format);
        case PERIOD.CUSTOM_START:
            return dayjs(customDate.startDate)
                .hour(0)
                .minute(0)
                .second(0)
                .format(format);
        case PERIOD.CUSTOM_END:
            return dayjs(customDate.endDate)
                .hour(23)
                .minute(59)
                .second(59)
                .format(format);

        default:
            return dayjs().format(format);
    }
}

export function getTimeAdjustedDate(hour, min, second) {
    return dayjs()
        .hour(hour)
        .minute(min)
        .second(second);
}

export function numberWithCommas(x) {
    var parts = x.toString().split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return parts.join('.');
}

export function arrayOfObjectsToIdsStr(selectedState) {
    const reducer = (accumulator, currentValue) => {
        return accumulator !== ''
            ? `${accumulator},${currentValue.id}`
            : currentValue.id;
    };
    if (selectedState && selectedState.length) {
        return selectedState.reduce(reducer, '');
    } else {
        return '';
    }
}

export function arrayOfObjectsToTitleStr(selectedState) {
    const reducer = (accumulator, currentValue) => {
        return accumulator !== ''
            ? `${accumulator},${currentValue.title}`
            : currentValue.title;
    };
    if (selectedState && selectedState.length) {
        return selectedState.reduce(reducer, '');
    } else {
        return '';
    }
}

export function unixTimestampToHumanReadable(timestamp) {
    return dayjs(parseInt(timestamp)).format(
        PERIOD.DAYJS_HUMAN_READABLE_FORMAT,
    );
}

export function unixTimestampToRelativeHumanReadable(
    timestamp,
    difference = 3,
    attribute = 'days',
) {
    const diff = Math.abs(
        dayjs(dayjs().format()).diff(parseInt(timestamp), attribute),
    );
    return diff > difference
        ? dayjs(parseInt(timestamp)).format(PERIOD.DAYJS_HUMAN_READABLE_FORMAT)
        : dayjs(parseInt(timestamp)).fromNow();
}

export function unixStringToHumanReadable(timestamp) {
    return dayjs(timestamp).format(PERIOD.DAYJS_HUMAN_READABLE_FORMAT);
}

export function secondsToHumanReadableDuration(seconds, isUppercase = true) {
    switch (true) {
        case seconds < 60.0:
            return `${seconds.toString()} ${getUppercaseString(
                MEASUREMENT_UNITS.SECONDS,
                isUppercase,
            )}`;
        case seconds >= 60.0 && seconds < 3600.0:
            return `${Math.floor(seconds / 60).toString()} ${getUppercaseString(
                MEASUREMENT_UNITS.MINUTES,
                isUppercase,
            )}`;
        case seconds >= 3600.0 && seconds < 7200.0:
            return `${Math.floor(
                seconds / (60 * 60),
            ).toString()} ${getUppercaseString(
                MEASUREMENT_UNITS.HOUR,
                isUppercase,
            )}`;
        case seconds >= 3600.0 && seconds < 86400.0:
            return `${Math.floor(
                seconds / (60 * 60),
            ).toString()} ${getUppercaseString(
                MEASUREMENT_UNITS.HOURS,
                isUppercase,
            )}`;
        case seconds >= 86400.0:
            return `${Math.floor(
                seconds / (60 * 60 * 24),
            ).toString()} ${getUppercaseString(
                MEASUREMENT_UNITS.DAYS,
                isUppercase,
            )}`;
        default:
            return `${seconds.toString()} ${getUppercaseString(
                MEASUREMENT_UNITS.SECONDS,
                isUppercase,
            )}`;
    }
}

export function metersToKiloMeters(meters, isUppercase = true) {
    switch (true) {
        case meters < 1000:
            return `${meters.toString()} ${getUppercaseString(
                MEASUREMENT_UNITS.METERS,
                isUppercase,
            )}`;
        case meters >= 1000:
            return `${(meters / 1000).toFixed(1)} ${getUppercaseString(
                MEASUREMENT_UNITS.KILOMETERS,
                isUppercase,
            )}`;
        default:
            return `${meters.toString()} ${getUppercaseString(
                MEASUREMENT_UNITS.METERS,
                isUppercase,
            )}`;
    }
}

export function getZoomLevelFromDistance(distance) {
    switch (true) {
        case distance < 100:
            return 18.0;
        case distance >= 100 && distance < 500:
            return 15.0;
        case (distance >= 500) & (distance < 1000):
            return 12.0;
        case distance > 1000:
            return 10.0;
        default:
            return 13.0;
    }
}

export function getActiveChallengeStatus(isPublished, expiresAt) {
    if (expiresAt > Date.now() && isPublished == true) {
        return true;
    } else if (expiresAt < Date.now() || isPublished == false) {
        return false;
    } else {
        return false;
    }
}

export const dateStringToStartEndDateObj = (dateStr) => {
    if (dateStr) {
        const endDate =
            dateStr === PERIOD.LAST_YEAR
                ? strPeriodToFormattedDate(PERIOD.LAST_YEAR_END)
                : strPeriodToFormattedDate(PERIOD.END_TODAY);
        return {
            startDate: strPeriodToFormattedDate(dateStr), // empty format will use ISO8601 by default
            endDate,
        };
    } else {
        return '';
    }
};
export function getUppercaseString(str, isUppercase) {
    return isUppercase ? str.toUpperCase() : str.toLowerCase();
}

export function chunk(array, size) {
    if (!array) return [];
    const firstChunk = array.slice(0, size); // create the first chunk of the given array
    if (!firstChunk.length) {
        return array; // this is the base case to terminal the recursive
    }
    return [firstChunk].concat(chunk(array.slice(size, array.length), size));
}
